import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { ValidEntitiesSelectionModalComponent } from '@connect-services/client/components/modals/field-intervention/valid-entities-selection-modal/valid-entities-selection-modal.component';
import { PaginationFooterTemplateComponent } from '@connect-services/client/components/pagination-footer-template/pagination-footer-template.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, LoadingComponent, PaginationFooterTemplateComponent, RessourcesModule],
    selector: 'app-create-field-intervention-footer',
    styleUrl: './create-field-intervention-footer.component.scss',
    templateUrl: './create-field-intervention-footer.component.html',
})
export class CreateFieldInterventionFooterComponent {
    protected readonly ButtonType = ButtonType;
    protected readonly createFieldInterventionService = inject(CreateFieldInterventionService);

    private readonly activateRoute = inject(ActivatedRoute);
    private readonly matDialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly selectionService = inject(SelectionService);
    private readonly snackBarService = inject(SnackBarService);

    public async handleSearch(searchValue: string): Promise<void> {
        this.createFieldInterventionService.resetPagination();
        this.createFieldInterventionService.searchValue.set(searchValue);
        await this.createFieldInterventionService.fetchEntities();
    }

    public openValidSelectionModal(): MatDialogRef<ValidEntitiesSelectionModalComponent> {
        const dialogRef = this.matDialog.open(ValidEntitiesSelectionModalComponent, {
            autoFocus: false,
            minWidth: '40vw',
        });

        dialogRef.afterClosed().subscribe(async () => {});

        return dialogRef;
    }

    public async validateSelection(): Promise<void> {
        const dialogRef = this.openValidSelectionModal();
        this.createFieldInterventionService.isSubmitting.set(true);

        try {
            await this.createFieldInterventionService.upsertFieldInterventionEntities();

            this.createFieldInterventionService.isSelectionEntitiesValidated.set(true);
            this.snackBarService.openSuccessSnackBar('fieldInterventionCreationSuccess');

            this.createFieldInterventionService.isSelectionEntitiesValidated.set(false);
            this.createFieldInterventionService.resetData();
            this.createFieldInterventionService.isSubmitting.set(false);

            dialogRef.close();

            await this.router.navigate([`../${RoutePathEnum.FIELD_INTERVENTION_PLANNING}`], {
                relativeTo: this.activateRoute,
            });
        } catch {
            this.createFieldInterventionService.isSubmitting.set(false);

            if (dialogRef.componentInstance) {
                dialogRef.componentInstance.errorMessage.set('fieldInterventionCreationError');
                setTimeout(() => {
                    dialogRef.close();
                }, 2000);
            }

            this.snackBarService.openErrorSnackBar('fieldInterventionCreationError');

            await this.selectionService.clearAllSelections();
        }
    }

    protected onPaginatorChange(paginator: PaginatorComponent): void {
        this.createFieldInterventionService.paginator = paginator;
    }
}
